import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import { filterMovies, resolveCategory } from "@/utils/movieUtils";
import { genreOptions, regionOptions, yearOptions } from "@/mocks/movies";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import NotFound from "@/pages/NotFound";

const PAGE_SIZE = 8;

const sortOptions = [
  { value: "hot", label: "热门度" },
  { value: "time", label: "上映时间" },
  { value: "rating", label: "评分" },
];

export default function CategoryPage() {
  const { type } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelOpen, setPanelOpen] = useState(false);
  const category = resolveCategory(type);

  const genre = searchParams.get("genre") || "";
  const region = searchParams.get("region") || "";
  const year = searchParams.get("year") || "";
  const sort = searchParams.get("sort") || "hot";
  const page = Number(searchParams.get("page") || "1");

  useDocumentTitle({
    title: category ? `${category.label}-屁屁影院` : "影视分类-屁屁影院",
    description: category
      ? `屁屁影院${category.label}频道，${category.desc}。为您持续更新最新${category.label}资源，支持按类型、地区、年代筛选。`
      : "屁屁影院影视分类频道，汇集电影、电视剧等丰富资源。",
    keywords: category ? `屁屁影院,${category.label},在线观看${category.label},${category.label}资源` : "屁屁影院,影视分类",
  });

  const result = useMemo(
    () => filterMovies({ category: category?.key, genre, region, year, sort }),
    [category?.key, genre, region, year, sort],
  );

  if (!category) {
    return <NotFound />;
  }

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = result.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setSearchParams(next);
  };

  const changePage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetFilters = () => setSearchParams({});

  const hasFilter = Boolean(genre || region || year);

  const filterGroups = [
    { label: "类型", key: "genre", value: genre, options: genreOptions },
    { label: "地区", key: "region", value: region, options: regionOptions },
    { label: "年代", key: "year", value: year, options: yearOptions },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto max-w-[1280px]">
          <nav className="mb-4 flex items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-950">{category.label}</span>
          </nav>

          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground-950 md:text-3xl">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500/15 text-primary-300">
                  <i className={category.icon} />
                </span>
                {category.label}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-foreground-500">{category.desc}，共 {result.length} 部作品。</p>
            </div>
            <button
              type="button"
              onClick={() => setPanelOpen((v) => !v)}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-950 md:hidden"
            >
              <i className="ri-filter-3-line" />
              {panelOpen ? "收起筛选" : "展开筛选"}
            </button>
          </div>

          <div className={`${panelOpen ? "block" : "hidden"} mb-6 rounded-lg border border-background-200/60 bg-background-100 p-4 md:block md:p-5`}>
            <div className="flex flex-col gap-4">
              {filterGroups.map((group) => (
                <div key={group.key} className="flex flex-col gap-2 md:flex-row md:items-center">
                  <span className="w-12 shrink-0 text-sm font-medium text-foreground-700">{group.label}</span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => updateParam(group.key, "")}
                      className={`rounded-full px-3 py-1 text-sm transition-colors ${
                        !group.value ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-600 hover:bg-background-300"
                      }`}
                    >
                      全部
                    </button>
                    {group.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => updateParam(group.key, opt)}
                        className={`rounded-full px-3 py-1 text-sm transition-colors ${
                          group.value === opt ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <span className="w-12 shrink-0 text-sm font-medium text-foreground-700">排序</span>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateParam("sort", opt.value)}
                      className={`rounded-full px-3 py-1 text-sm transition-colors ${
                        sort === opt.value ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600 hover:bg-background-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {hasFilter && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">已选筛选：</span>
              {[
                { k: "genre", v: genre },
                { k: "region", v: region },
                { k: "year", v: year },
              ]
                .filter((x) => x.v)
                .map((x) => (
                  <button
                    key={x.k}
                    type="button"
                    onClick={() => updateParam(x.k, "")}
                    className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                  >
                    {x.v}
                    <i className="ri-close-line" />
                  </button>
                ))}
              <button type="button" onClick={resetFilters} className="text-xs text-accent-400 hover:text-accent-300">
                重置筛选
              </button>
            </div>
          )}

          {pageItems.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/60 bg-background-100 py-20 text-center">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-inbox-line text-3xl" />
              </span>
              <h3 className="text-lg font-semibold text-foreground-950">没有找到符合条件的影视</h3>
              <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-accent-600"
              >
                重置筛选
              </button>
            </div>
          )}

          <Pagination current={safePage} total={totalPages} onChange={changePage} />
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}